import { UpdateVarsRequest, VarRule } from '@fullstory/proto/fs/services/fieldmeta';
import { CustomField, FieldScope } from './types';

type VarUpdate = UpdateVarsRequest.VarUpdate;

export type VarSet = Set<string>;

/**
 * Returns the key used to lookup a custom field in the hidden vars map.
 *
 * Hidden var keys look like:
 * Block for a non-event field
 *   <scope>_<ident>_<type>
 *
 * Block for a specific connected property.
 *   <scope>_<ident>_<type>:<parent>
 *
 */
function fieldHideKey(field: CustomField, parent: string | number): string {
  let scopedFieldName = [field.scope, field.ident, field.varType].join('_');
  if (parent) {
    scopedFieldName = scopedFieldName + parentHideKey(parent);
  }
  return scopedFieldName;
}

/**
 * Returns the key used to lookup a parent custom field in the hidden vars map.
 *
 * Hidden var keys look like:
 * Block for a non-event field
 *   <scope>_<partial ident>
 *
 * Block for a specific custom event property.
 *   <scope>_<partial ident>:<parent>
 *
 * All properties that are children of the parent ident will be hidden.
 */
export const partialFieldKey = (
  scope: FieldScope,
  ident: string,
  parent?: string | number,
): string => {
  const parentSuffix = parent ? parentHideKey(parent) : '';
  return [scope, ident].join('_') + parentSuffix;
};

const varRuleTypeToPrefix = new Map<VarRule.VarType, string>([
  [VarRule.VarType.VAR_TYPE_USER_PROPERTY, `${FieldScope.User}_`],
  [VarRule.VarType.VAR_TYPE_EVENT_PROPERTY, `${FieldScope.Event}_`],
  [VarRule.VarType.VAR_TYPE_PAGE_PROPERTY, `${FieldScope.Page}_`],
  [VarRule.VarType.VAR_TYPE_ELEMENT_PROPERTY, `${FieldScope.Element}_`],
  [VarRule.VarType.VAR_TYPE_EVENT_NAME, ':'],
]);

export function varUpdateToHideKey(update: VarUpdate): string {
  const prefix = varRuleTypeToPrefix.get(update.type) ?? '';
  const suffix = update.parentPattern ? `:${update.parentPattern}` : '';
  return `${prefix}${update.pattern}${suffix}`;
}

/** Returns the hide key used to block an entire custom event from showing in the UI. */
export function parentHideKey(parent: string | number): string {
  return `:${parent}`;
}

/**
 * Returns true if the field is hidden based on the provided hidden vars.
 *
 * For property fields, this checks whether the prop should be hidden due to an explicit hide or due to
 * to "inheriting" the state from a a parent property (w/ object structure, e.g. "obj" would block "obj.name_str").
 *
 * Additionally, for custom event properties, the prop is also hidden if the owned event is hidden as a whole.
 *
 * @param field - the `CustomField` to test
 * @param parent - the parent (or empty if none) of the custom field to use for checking hidden status
 * @param hiddenVars - the set of hidden vars to test
 */
export const isFieldHidden = (
  field: CustomField,
  parent: string | number,
  hiddenVars: VarSet,
): boolean => {
  // first, check explicit full value
  if (hiddenVars.has(fieldHideKey(field, parent))) return true;

  // optionally, check parent event name (if correct scope)
  // events are the only parent that is currently managed through hidden vars
  if (field.scope == FieldScope.Event && parent) {
    if (hiddenVars.has(parentHideKey(parent))) return true;
  }

  // finally, check object like prefixes
  const suffixedIdent = [field.ident, field.varType].join('_');
  let identPrefix = '';
  for (const property of suffixedIdent.split('.')) {
    identPrefix = [identPrefix, property].filter(Boolean).join('.');
    if (isIdentInSet(hiddenVars, field.scope, identPrefix, parent)) return true;
    if (parent && isIdentInSet(hiddenVars, field.scope, identPrefix)) return true; // also try w/o parent if exists
  }
  return false;
};

/**
 * Returns true if the given identifier is hidden.
 *
 * isFieldHidden returns true if the ident or any parent ident is hidden.  isIdentHidden only returns true if the ident itself is hidden.
 * This distinction is useful in settings, specifically the view of archived fields.  You only want to show the unarchive action for the
 * idents that are hidden.
 */
export const isIdentInSet = (
  hiddenVars: VarSet,
  scope: FieldScope,
  ident: string,
  parent?: string | number,
): boolean => hiddenVars.has(partialFieldKey(scope, ident, parent));

export type VarDescription = Pick<VarUpdate, 'type' | 'pattern' | 'parentPattern'>;

export interface PropHiddenState {
  /** The key used to check existence within the hiddenVars set */
  hideKey: string;
  varDesc: VarDescription;
  /**  True if this prop has any parent that is in the hiddenVars set */
  parentHidden: boolean;
  /** True, if this prop is named in the hiddenVars set */
  selfHidden: boolean;
}

/**
 * Maps the callback onto each of the parents of the CustomField.
 *
 * Custom event properties can be arbitrarily nested. The prop will look like
 * `evt_outer.inner.data_str`. This function will traverse the hierarchy from top
 * down and call the callback on each parent. In the callback, `name` refers to the
 * "split" name (i.e. `inner`), ref refers to the full path (i.e. `outer.inner`), and depth
 * refers to the current level of the hierarchy.
 *
 * @param field - The `CustomField` for which to map the Parents
 * @param cb - function to be called on each parent
 */
export function mapParents<U>(
  field: CustomField,
  cb: (name: string, ref: string, depth: number) => U,
): U[] {
  const keys = field.ident.split('.');
  return keys.slice(0, keys.length - 1).map((k, j) => {
    const ref = keys.slice(0, j + 1).join('.');
    return cb(k, ref, j + 1);
  });
}
